import React, { useMemo, useState } from 'react';
import { Grid, CircularProgress } from '@material-ui/core';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { PrimaryButton, SecondaryButton } from '@/styles/blocks';
import { css } from '@emotion/react';
import { colors } from '@/styles';
import { Header1 } from './blocks';
import SubregionListItem from './SubregionListItem';
import DeleteSubregionModal from './DeleteSubregionModal';
import Papa from 'papaparse';
import { SUBREGION_IMPORT_REQ_COLUMNS } from './constants';
import SearchInput from '../misc/SearchInput';
import { captureException } from '@sentry/react';

const SubregionList = ({ subregions, selected, loading, filter, callbacks, options }) => {
    const [editing, setEditing] = useState(null);
    // The subregion awaiting delete confirmation, under its display name.
    const [deleting, setDeleting] = useState(null);

    const list = useMemo(() => {
        return subregions
            .map((subregion, idx) => [subregion, idx])
            .filter(([subregion, idx]) => {
                return (
                    !filter.subregion ||
                    callbacks.genName(subregion, idx)?.toLowerCase()?.includes(filter.subregion.toLowerCase()) ||
                    callbacks.genOwner(subregion, idx)?.toLowerCase()?.includes(filter.subregion.toLocaleLowerCase())
                );
            });
    }, [subregions, filter]);

    return (
        <>
            <Grid
                container
                css={css`
                    flex: 0 0 auto;
                    padding: 12px 20px;
                    background: ${colors.white.secondary};
                    border-top: 1px solid ${colors.greys.border};
                    border-bottom: 1px solid ${colors.greys.border};
                `}
            >
                <Header1
                    css={css`
                        font-size: 16px;
                        font-weight: 700;
                    `}
                >
                    Subregions
                </Header1>
            </Grid>
            <Grid
                container
                direction="row"
                css={css`
                    flex: 0;
                    flex-basis: 0;
                    flex-wrap: nowrap;
                    padding: 12px 20px;
                `}
            >
                <SearchInput
                    placeholder="Search Subregions"
                    onChange={(search) => callbacks.setFilter((prev) => ({ ...prev, subregion: search }))}
                />
            </Grid>
            <Grid
                container
                direction="row"
                css={css`
                    /* No inner scroll: the column scrolls, so the footer sits under the last card. */
                    flex: 0 0 auto;
                    border-top: 1px solid ${colors.greys[4]};
                `}
            >
                <Grid container direction="column">
                    {list.map(([subregion, idx]) => {
                        return (
                            <SubregionListItem
                                key={subregion.subregion_id}
                                idx={idx}
                                isEditing={subregion.subregion_id === editing}
                                isSelected={selected?.subregion_id === subregion.subregion_id}
                                subregion={subregion}
                                options={options}
                                callbacks={{
                                    ...callbacks,
                                    toggleEdit: (id) => {
                                        setEditing((prev) => (prev === id ? null : id));
                                    },
                                    setEdit: (id, status = false) => {
                                        setEditing((prev) => (status ? id : null));
                                    },
                                    resetEdit: () => {
                                        setEditing(null);
                                    },
                                    confirmDelete: (target, label) => setDeleting({ target, name: label }),
                                }}
                            />
                        );
                    })}
                </Grid>
            </Grid>
            {/* Outside the rows: clicks in the dialog would bubble to a row and select it. */}
            <DeleteSubregionModal
                subregion={deleting ? { ...deleting.target, name: deleting.name } : null}
                onCancel={() => setDeleting(null)}
                onDelete={() => {
                    callbacks.deleteSubregion(deleting.target);
                    setDeleting(null);
                }}
            />
            <Grid
                container
                direction="column"
                css={css`
                    flex: 0 0 auto;
                    gap: 10px;
                    padding: 16px 20px;
                    border-top: 1px solid ${colors.greys[4]};
                `}
            >
                {!options?.readOnly ? (
                    <PrimaryButton
                        fullWidth
                        disabled={!options?.enableAddSubregions || loading.subregions}
                        onClick={() =>
                            callbacks.onAddSubregion ? callbacks.onAddSubregion() : callbacks.createSubregion({})
                        }
                        css={css`
                            height: 40px;
                        `}
                    >
                        Add Subregion
                    </PrimaryButton>
                ) : null}
                <Grid
                    container
                    css={css`
                        flex-wrap: nowrap;
                        gap: 10px;
                    `}
                >
                    {!options?.readOnly ? (
                        <SecondaryButton
                            fullWidth
                            component="label"
                            disabled={!options?.enableImport || loading.import}
                            startIcon={
                                loading.import ? <CircularProgress size="18px" /> : <FileUploadIcon fontSize="small" />
                            }
                            css={css`
                                min-width: 0;
                            `}
                        >
                            Import
                            <input
                                disabled={!options?.enableImport || loading.import || loading.init}
                                type="file"
                                hidden
                                accept=".csv"
                                onChange={(e) => {
                                    const file = e.target.files?.[0];
                                    Papa.parse(file, {
                                        complete: ({ data }) => {
                                            const [headers, ...body] = data;
                                            if (
                                                SUBREGION_IMPORT_REQ_COLUMNS.some((header) => !headers.includes(header))
                                            ) {
                                                callbacks.setNotification({
                                                    severity: 'error',
                                                    message: `CSV missing required columns: ${SUBREGION_IMPORT_REQ_COLUMNS.join(
                                                        ', '
                                                    )}`,
                                                });
                                                return;
                                            }

                                            callbacks.importSubregions(headers, body);
                                        },
                                        error: (e) => {
                                            console.error(e);
                                            captureException(e);
                                            callbacks.setNotification({
                                                severity: 'error',
                                                message: `Error processing CSV file: ${e.message}`,
                                            });
                                        },
                                    });
                                }}
                            />
                        </SecondaryButton>
                    ) : null}
                    <SecondaryButton
                        fullWidth
                        disabled={!options?.enableExport || loading.export || loading.init}
                        onClick={() => callbacks.exportSubregions()}
                        startIcon={<FileDownloadIcon fontSize="small" />}
                        css={css`
                            min-width: 0;
                        `}
                    >
                        Export
                    </SecondaryButton>
                </Grid>
            </Grid>
        </>
    );
};

export default SubregionList;
